// components/Form2307CertificatesPanel.jsx
//
// The Form 2307 tab of VAT Management (/client/tax-obligation).
//
// Read-only by design. Every certificate here was derived automatically from
// posting a VAT transaction that carried an ATC, so there is no "Generate"
// action — that was the second, redundant encoding this feature removed. The
// tab shows what posting produced and lets staff download or reprint it.
//
// Corrections and reissues still exist, but they live on the Form 2307
// obligation screen's Correction / Reissue tab, not here.
//
// NOT named Form2307List: that component already exists, is currently imported
// nowhere, and renders a stale record shape (payerName/grossAmount/
// withheldRate/withheldAmount) that no longer matches Form2307Record
// (vendor/baseAmount/withholdingRate/taxWithheld). It is also edit-oriented,
// and editing an auto-generated certificate is refused by the immutability
// guard on the model. Left untouched rather than repurposed.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import form2307API from '../api/form2307API';
import Button from './Button';
import notify from '../lib/notify';

const QUARTER_MONTHS = {
  1: [1, 2, 3],
  2: [4, 5, 6],
  3: [7, 8, 9],
  4: [10, 11, 12],
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Integer centavos -> '1,234.56'. Falls back to the peso column on older rows. */
const formatCentavos = (centavos, pesoFallback) => {
  const value = centavos === null || centavos === undefined
    ? Math.round((Number(pesoFallback) || 0) * 100)
    : Number(centavos) || 0;
  return (value / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Integer basis points -> '5.00%'. */
const formatRate = (basisPoints, percentFallback) => {
  const bp = basisPoints === null || basisPoints === undefined
    ? Math.round((Number(percentFallback) || 0) * 100)
    : Number(basisPoints) || 0;
  return `${(bp / 100).toFixed(2)}%`;
};

const centavosOf = (cert) =>
  cert.taxWithheldCentavos === null || cert.taxWithheldCentavos === undefined
    ? Math.round((Number(cert.taxWithheld) || 0) * 100)
    : Number(cert.taxWithheldCentavos) || 0;

const baseCentavosOf = (cert) =>
  cert.baseAmountCentavos === null || cert.baseAmountCentavos === undefined
    ? Math.round((Number(cert.baseAmount) || 0) * 100)
    : Number(cert.baseAmountCentavos) || 0;

const Form2307CertificatesPanel = ({ client, selectedPeriod }) => {
  const clientId = client?._id;
  const quarter = selectedPeriod?.quarter || null;
  const fiscalYear = selectedPeriod?.year || null;
  const month = selectedPeriod?.month || null;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [includeVoided, setIncludeVoided] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const fetchRecords = useCallback(async () => {
    if (!clientId || !fiscalYear) {
      setRecords([]);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const res = await form2307API.list(clientId, {
        fiscalYear,
        // Month narrows within the quarter when the period picker has one set;
        // otherwise the whole quarter is shown, which is the filing unit.
        ...(month ? { month } : quarter ? { quarter } : {}),
        ...(includeVoided ? { includeVoided: true } : {}),
      });

      setRecords(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load Form 2307 records', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to load Form 2307 records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, fiscalYear, quarter, month, includeVoided]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const totals = useMemo(() => {
    const live = records.filter((r) => !r.isVoided);
    return {
      count: live.length,
      baseCentavos: live.reduce((sum, r) => sum + baseCentavosOf(r), 0),
      taxCentavos: live.reduce((sum, r) => sum + centavosOf(r), 0),
    };
  }, [records]);

  // Grouped for the quarterly batch download: BIR expects one 2307 per
  // counterparty per quarter, not one per transaction line.
  const counterparties = useMemo(() => {
    const map = new Map();
    for (const cert of records) {
      if (cert.isVoided) continue;
      const vendor = cert.vendor;
      const id = typeof vendor === 'object' && vendor ? vendor._id : vendor;
      if (!id) continue;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: (typeof vendor === 'object' && vendor?.name) || 'Unnamed counterparty',
          count: 0,
          taxCentavos: 0,
        });
      }
      const entry = map.get(key);
      entry.count += 1;
      entry.taxCentavos += centavosOf(cert);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [records]);

  const handleDownload = async (cert) => {
    const key = `single:${cert._id}`;
    try {
      setDownloading(key);
      await form2307API.downloadPdf(clientId, cert._id, `form-2307-${cert.certificateNumber || cert._id}.pdf`);
    } catch (e) {
      notify.error(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const handleQuarterlyDownload = async (counterparty) => {
    const key = `batch:${counterparty.id}`;
    try {
      setDownloading(key);
      await form2307API.downloadQuarterlyPdf(clientId, counterparty.id, { quarter, fiscalYear });
    } catch (e) {
      notify.error(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const periodLabel = month
    ? `${MONTH_NAMES[month - 1]} ${fiscalYear}`
    : quarter
      ? `Q${quarter} ${fiscalYear} (${QUARTER_MONTHS[quarter].map((m) => MONTH_NAMES[m - 1].slice(0, 3)).join(', ')})`
      : `${fiscalYear || ''}`;

  if (!clientId) {
    return <div className="p-6 text-sm text-ink-body">No client selected.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-ink">Form 2307 — Creditable Tax Withheld</h3>
          <p className="text-sm text-ink-body mt-0.5">
            Generated automatically when a VAT transaction with an ATC is posted. {periodLabel}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-body">
            <input
              type="checkbox"
              checked={includeVoided}
              onChange={(e) => setIncludeVoided(e.target.checked)}
              className="rounded border-ink-line text-primary-500 focus:ring-primary-500"
            />
            Show voided
          </label>
          <Button variant="outline" size="sm" onClick={fetchRecords} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-500 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-ink-line p-4">
          <div className="text-xs text-ink-body">Certificates</div>
          <div className="text-lg font-medium text-ink tabular-nums">{totals.count}</div>
        </div>
        <div className="rounded-lg border border-ink-line p-4">
          <div className="text-xs text-ink-body">Income payments</div>
          <div className="text-lg font-medium text-ink tabular-nums">₱{formatCentavos(totals.baseCentavos)}</div>
        </div>
        <div className="rounded-lg border border-ink-line p-4">
          <div className="text-xs text-ink-body">Tax withheld</div>
          <div className="text-lg font-medium text-primary-700 tabular-nums">₱{formatCentavos(totals.taxCentavos)}</div>
        </div>
      </div>

      {/* Quarterly batch per counterparty */}
      {quarter && counterparties.length > 0 && (
        <div className="rounded-lg border border-ink-line">
          <div className="border-b border-ink-line px-4 py-3">
            <h4 className="text-sm font-medium text-ink">Quarterly certificate per counterparty</h4>
            <p className="text-xs text-ink-body mt-0.5">
              One Form 2307 covering the whole quarter, which is what BIR expects per counterparty.
            </p>
          </div>
          <ul className="divide-y divide-ink-line">
            {counterparties.map((cp) => (
              <li key={cp.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <div className="text-sm text-ink">{cp.name}</div>
                  <div className="text-xs text-ink-body">
                    {cp.count} {cp.count === 1 ? 'line' : 'lines'} · ₱{formatCentavos(cp.taxCentavos)} withheld
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuarterlyDownload(cp)}
                  loading={downloading === `batch:${cp.id}`}
                  disabled={!!downloading}
                >
                  Download Q{quarter}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-transaction certificates */}
      <div className="rounded-lg border border-ink-line overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-line">
            <thead className="bg-ink-canvas">
              <tr>
                {['Certificate no.', 'Certificate date', 'Counterparty', 'ATC', 'Income payment', 'Rate', 'Tax withheld', ''].map((h, i) => (
                  <th
                    key={h || `action-${i}`}
                    scope="col"
                    className={`px-4 py-2 text-xs font-medium text-ink-body ${
                      ['Income payment', 'Rate', 'Tax withheld'].includes(h) ? 'text-right' : 'text-left'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-line">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-sm text-ink-body text-center">Loading…</td>
                </tr>
              )}

              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-sm text-ink-body text-center">
                    No Form 2307 for this period. One is created automatically when you post a
                    transaction on the Output or Input tab with an ATC selected.
                  </td>
                </tr>
              )}

              {!loading && records.map((cert) => (
                <tr key={cert._id} className={cert.isVoided ? 'opacity-60' : undefined}>
                  <td className="px-4 py-3 text-sm text-ink whitespace-nowrap">
                    <span className="tabular-nums">{cert.certificateNumber || '—'}</span>
                    {cert.isVoided && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-md bg-danger-50 text-danger-700">
                        Voided
                      </span>
                    )}
                  </td>
                  {/*
                    certificateDate, NOT transactionDate. The certificate is
                    dated the last day of the month selected on the Output/Input
                    form (August -> 08/31), matching its GL posting. The source
                    record's transactionDate is the moment it was encoded, which
                    is why this column used to read as "today".
                  */}
                  <td className="px-4 py-3 text-sm text-ink-body whitespace-nowrap">
                    {cert.certificateDate ? new Date(cert.certificateDate).toLocaleDateString('en-PH') : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-ink">
                    {(typeof cert.vendor === 'object' && cert.vendor?.name) || '—'}
                    {typeof cert.vendor === 'object' && cert.vendor?.businessType === 'government' && (
                      <span className="block text-xs text-ink-body">Government</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-ink whitespace-nowrap">{cert.atcCode || '—'}</td>
                  <td className="px-4 py-3 text-sm text-ink text-right tabular-nums whitespace-nowrap">
                    {formatCentavos(cert.baseAmountCentavos, cert.baseAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-body text-right tabular-nums whitespace-nowrap">
                    {formatRate(cert.rateBasisPoints, cert.withholdingRate)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-ink text-right tabular-nums whitespace-nowrap">
                    {formatCentavos(cert.taxWithheldCentavos, cert.taxWithheld)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(cert)}
                      loading={downloading === `single:${cert._id}`}
                      disabled={!!downloading}
                    >
                      {cert.isVoided ? 'Reprint' : 'Download'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Form2307CertificatesPanel;
